import React from "react";
import { shallowEqual } from "react-redux";
import { useAppSelector } from "@redux/hooks";
import { OrderCurrency } from "../orderCurrency";

/**
 * Company base currency: the currency of new orders by default and of orders saved before
 * currencies were supported (`currency: null`).
 *
 * Compared shallowly, so that reloading the user keeps the same reference: the order form
 * hydration depends on it and would otherwise reset the form.
 */
export function useBaseCurrency() {
    const baseCurrency = useAppSelector(
        (state) => state.auth.user?.company?.currency,
        shallowEqual
    );

    if (!baseCurrency) {
        throw new Error("The company base currency is not loaded.");
    }

    return baseCurrency;
}

/**
 * Currency of the order being edited or viewed.
 *
 * Amounts are formatted and base prices converted deep inside both the editor (the add product
 * modal, its margin hint, the BOM selector) and the shared margin components. A context keeps the
 * currency out of every prop list on the way there. The editor provides it from the form, the
 * saved order view from the order itself.
 */
const OrderCurrencyContext = React.createContext<OrderCurrency | null>(null);

export function OrderCurrencyProvider({
    children,
    value,
}: React.PropsWithChildren<{ value: OrderCurrency }>) {
    return <OrderCurrencyContext.Provider value={value}>{children}</OrderCurrencyContext.Provider>;
}

export function useOrderCurrency(): OrderCurrency {
    const orderCurrency = React.useContext(OrderCurrencyContext);

    // Falling back to the base currency here would show a foreign order in the wrong units
    if (!orderCurrency) {
        throw new Error("useOrderCurrency must be used within an OrderCurrencyProvider.");
    }

    return orderCurrency;
}
